import styled from "styled-components";

const SectionWrapper = styled.div`
  padding: 80px 0 60px;
  background-color: #fcfcfc;
  position: relative;
  overflow: hidden;

  /* Section Header Styling */
  .section-header {
    text-align: center;
    margin-bottom: 50px;
    position: relative;
    z-index: 3;

    .subtitle {
      font-size: 14px;
      font-weight: 800;
      color: #D87B1C;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 10px;
      display: block;
    }

    .title {
      font-size: 38px;
      font-weight: 800;
      color: #1c1917;
      margin-bottom: 15px;

      span.btn_heading {
        background: none;
        color: #D87B1C;
        padding: 0;
        font-weight: 800;
      }
    }

    .title-divider {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      margin: 15px 0 20px;

      &::before,
      &::after {
        content: '';
        width: 60px;
        height: 1px;
        background-color: rgba(216, 123, 28, 0.3);
      }

      .dot {
        width: 6px;
        height: 6px;
        background-color: #D87B1C;
        border-radius: 50%;
      }
    }

    .description {
      font-size: 17px;
      color: #666;
      max-width: 600px;
      margin: 0 auto;
      font-weight: 500;
      line-height: 1.5;
    }
  }

  /* Main Grid Layout */
  .main-content-grid {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1400px;
    margin: 0 auto;
    gap: 20px;
    position: relative;
    z-index: 2;
  }

  .left-column {
    width: 32%;
    display: flex;
    flex-direction: column;
    gap: 40px;
    align-items: flex-end; /* Align cards towards the center head */
  }

  .right-column {
    width: 32%;
    display: flex;
    flex-direction: column;
    gap: 40px;
    align-items: flex-start; /* Align cards towards the center head */
  }

  .center-column {
    width: 36%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding: 20px 0;
  }

  /* Concentric Circles & Radial Glow Background (Relative to slider-circle-wrapper) */
  .center-bg-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(216, 123, 28, 0.12) 0%, rgba(216, 123, 28, 0) 70%);
    z-index: 0;
    pointer-events: none;
  }

  .center-bg-lines {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 580px;
    height: 580px;
    z-index: 0;
    pointer-events: none;

    .circle {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border: 1.5px dashed rgba(216, 123, 28, 0.15);
      border-radius: 50%;
      
      &:nth-child(1) {
        width: 360px;
        height: 360px;
      }
      &:nth-child(2) {
        width: 480px;
        height: 480px;
      }
      &:nth-child(3) {
        width: 580px;
        height: 580px;
      }
    }

    .dot-marker {
      position: absolute;
      width: 8px;
      height: 8px;
      background-color: #D87B1C;
      border-radius: 50%;
      box-shadow: 0 0 10px rgba(216, 123, 28, 0.5);
      z-index: 1;

      &.dot-1 {
        top: 22%;
        left: 22%;
      }
      &.dot-2 {
        top: 78%;
        right: 22%;
      }
      &.dot-3 {
        top: 50%;
        left: 8%;
      }
      &.dot-4 {
        top: 38%;
        right: 12%;
      }
    }
  }

  /* Interactive Feature Cards */
  .feature-card {
    background: #ffffff;
    border-radius: 24px;
    padding: 32px 28px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.04);
    position: relative;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    z-index: 2;
    text-decoration: none !important;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
    max-width: 300px; /* Make cards compact exactly like mockup */

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(216, 123, 28, 0.12);
      border-color: rgba(216, 123, 28, 0.2);
    }

    .icon-badge {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #FFFAF1;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
      transition: all 0.3s ease;
      color: #D87B1C;
      flex-shrink: 0;

      svg {
        width: 28px;
        height: 28px;
        transition: transform 0.3s ease;
      }
    }

    &:hover .icon-badge {
      background: #D87B1C;
      color: #ffffff;
      svg {
        transform: scale(1.1);
      }
    }

    .card-title {
      font-size: 19px;
      font-weight: 700;
      color: #1c1917;
      margin-bottom: 12px;
      position: relative;
      display: inline-block;

      &::after {
        content: '';
        display: block;
        width: 25px;
        height: 2px;
        background-color: #D87B1C;
        margin: 8px auto 0;
        transition: width 0.3s ease;
      }
    }

    &:hover .card-title::after {
      width: 45px;
    }

    .card-desc {
      font-size: 14px;
      color: #666666;
      line-height: 1.6;
      margin: 0;
    }

    /* Orange side indicators pointing towards the center (pill shape) */
    &.align-left {
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 6px;
        height: 40px;
        background-color: #D87B1C;
        border-radius: 4px 0 0 4px;
        transition: height 0.3s ease;
      }
      &:hover::after {
        height: 60px;
      }
    }

    &.align-right {
      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 6px;
        height: 40px;
        background-color: #D87B1C;
        border-radius: 0 4px 4px 0;
        transition: height 0.3s ease;
      }
      &:hover::after {
        height: 60px;
      }
    }
  }

  /* Slider Frame */
  .slider-circle-wrapper {
    position: relative;
    z-index: 2;
    // margin-bottom: 35px;
    // width: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .slider-circle {
    width: 100%;
    position: relative;
    z-index: 2;
    border-radius: 0 !important;
    overflow: hidden !important;

    img-comparison-slider {
      width: 100%;
      outline: none;

      img {
        width: 100%;
        height: auto;
        display: block;

        &[slot="second"] {
          transform: translate(-7.14%, -2 .86%) scale(1.08);
          transform-origin: top left;
        }
      }
    }
  }

  /* Wide Bottom Card Layout */
  .wide-bottom-card {
    width: 100%;
    max-width: 480px;
    background: linear-gradient(135deg, #FFFAF1 0%, #FAF2E6 100%);
    border-radius: 20px;
    padding: 22px 26px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(216, 123, 28, 0.15);
    display: flex;
    align-items: center;
    gap: 20px;
    position: relative;
    z-index: 2;
    text-decoration: none !important;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(216, 123, 28, 0.12);
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 6px;
      height: 40px;
      background-color: #D87B1C;
      border-radius: 0 4px 4px 0;
      transition: height 0.3s ease;
    }

    &:hover::after {
      height: 60px;
    }

    .icon-badge {
      width: 54px;
      height: 54px;
      border-radius: 50%;
      background: #ffffff;
      box-shadow: 0 5px 15px rgba(216, 123, 28, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.3s ease;
      color: #D87B1C;

      svg {
        width: 26px;
        height: 26px;
      }
    }

    &:hover .icon-badge {
      background: #D87B1C;
      color: #ffffff;
    }

    .card-content {
      flex-grow: 1;

      .card-title {
        font-size: 18px;
        font-weight: 700;
        color: #1c1917;
        margin-bottom: 6px;
        display: inline-block;

        &::after {
          content: '';
          display: block;
          width: 20px;
          height: 2px;
          background-color: #D87B1C;
          margin-top: 4px;
        }
      }

      .card-desc {
        font-size: 13.5px;
        color: #666666;
        line-height: 1.5;
        margin: 0;
      }
    }
  }

  /* Feature Footer Bar */
  .footer-feature-bar {
    max-width: 1200px;
    margin: 60px auto 0;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px 15px;
    padding-top: 40px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    position: relative;
    z-index: 2;

    .feature-item {
      display: flex;
      align-items: center;
      gap: 10px;

      .check-icon {
        width: 20px;
        height: 20px;
        background-color: #D87B1C;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 10px;
        font-weight: bold;
        flex-shrink: 0;

        svg {
          width: 10px;
          height: 10px;
          stroke: #ffffff;
          stroke-width: 3.5;
          fill: none;
        }
      }

      .feature-text {
        font-size: 16px;
        font-weight: 700;
        color: #333333;
        letter-spacing: 0.2px;
      }
    }

    .divider {
      width: 1.5px;
      height: 20px;
      background-color: rgba(0, 0, 0, 0.1);

      @media only screen and (max-width: 991px) {
        display: none;
      }
    }
  }

  /* Responsive Adjustments */
  @media only screen and (max-width: 1200px) {
    .main-content-grid {
      padding: 0 20px;
    }
    .slider-circle-wrapper {
      width: 280px;
    }
    .slider-circle {
      width: 100%;
      height: auto;
    }
    .center-bg-lines {
      width: 500px;
      height: 500px;
      .circle {
        &:nth-child(1) { width: 310px; height: 310px; }
        &:nth-child(2) { width: 420px; height: 420px; }
        &:nth-child(3) { width: 500px; height: 500px; }
      }
    }
  }

  @media only screen and (max-width: 991px) {
    padding: 60px 0 40px;

    .section-header {
      .title {
        font-size: 32px;
      }
    }

    .main-content-grid {
      flex-direction: column;
      gap: 30px;
    }

    .left-column,
    .right-column {
      width: 100%;
      max-width: 600px;
      gap: 25px;
      align-items: center;
    }

    .center-column {
      width: 100%;
      order: -1;
      padding: 20px 0;
    }

    .feature-card {
      max-width: 100%;
      &.align-left::after,
      &.align-right::after {
        left: 0;
        right: auto;
        border-radius: 0 4px 4px 0;
      }
    }

    .wide-bottom-card {
      max-width: 600px;
    }
  }

  @media only screen and (max-width: 767px) {
    padding: 50px 0 30px;

    .section-header {
      margin-bottom: 35px;
      .title {
        font-size: 26px;
      }
      .description {
        font-size: 15px;
        padding: 0 15px;
      }
    }

    .slider-circle-wrapper {
      width: 250px;
    }
    .slider-circle {
      width: 100%;
      height: auto;
    }

    .center-bg-lines {
      display: none;
    }

    .feature-card {
      padding: 20px;
      .card-title {
        font-size: 17px;
      }
      .card-desc {
        font-size: 13.5px;
      }
    }

    .footer-feature-bar {
      flex-direction: column;
      align-items: flex-start;
      padding-left: 20px;
      gap: 15px;
      margin-top: 40px;

      .divider {
        display: none;
      }
    }
  }
`;

export const Container = styled.div`
  max-width: 1627px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media only screen and (max-width: 1700px) {
    padding: 0 70px;
  }
  @media only screen and (max-width: 1360px) {
    padding: 0 40px;
  }
  @media only screen and (max-width: 767px) {
    padding: 0 15px;
  }
`;

export default SectionWrapper;
